import React, { useCallback, useEffect, useMemo, useState } from 'react'

import CustomSelect, {
  SelectOption,
} from '../../../component/customSelect/customSelect'
import { listAllHierarchyItems } from '../../../api/subject'
import {
  HierarchyItem,
  SubjectDetail,
  labelAt,
} from './hierarchyUtils'

export type HierarchyAncestorFilterValues = {
  level_1_id: string
  level_2_id: string
  level_3_id: string
}

export const EMPTY_ANCESTOR_FILTERS: HierarchyAncestorFilterValues = {
  level_1_id: '',
  level_2_id: '',
  level_3_id: '',
}

type HierarchyAncestorFiltersProps = {
  subject: SubjectDetail
  level: number
  values: HierarchyAncestorFilterValues
  onChange: (values: HierarchyAncestorFilterValues) => void
}

const HierarchyAncestorFilters: React.FC<HierarchyAncestorFiltersProps> = ({
  subject,
  level,
  values,
  onChange,
}) => {
  const labels = subject.level_labels
  const [options, setOptions] = useState<Record<number, SelectOption[]>>({
    1: [],
    2: [],
    3: [],
  })

  const loadOptions = useCallback(
    async (targetLevel: number, parentId?: string) => {
      try {
        const items = (await listAllHierarchyItems(subject.id, {
          level: targetLevel,
          parent_id: parentId,
        })) as HierarchyItem[]
        return items.map(item => ({ value: item.id, label: item.name }))
      } catch {
        return []
      }
    },
    [subject.id]
  )

  useEffect(() => {
    let cancelled = false
    loadOptions(1).then(opts => {
      if (!cancelled) setOptions(prev => ({ ...prev, 1: opts }))
    })
    return () => {
      cancelled = true
    }
  }, [loadOptions])

  useEffect(() => {
    let cancelled = false
    if (level < 3 || !values.level_1_id) {
      setOptions(prev => ({ ...prev, 2: [] }))
      return
    }
    loadOptions(2, values.level_1_id).then(opts => {
      if (!cancelled) setOptions(prev => ({ ...prev, 2: opts }))
    })
    return () => {
      cancelled = true
    }
  }, [level, values.level_1_id, loadOptions])

  useEffect(() => {
    let cancelled = false
    if (level < 4 || !values.level_2_id) {
      setOptions(prev => ({ ...prev, 3: [] }))
      return
    }
    loadOptions(3, values.level_2_id).then(opts => {
      if (!cancelled) setOptions(prev => ({ ...prev, 3: opts }))
    })
    return () => {
      cancelled = true
    }
  }, [level, values.level_2_id, loadOptions])

  const filterFields = useMemo(() => {
    const fields: Array<{
      key: keyof HierarchyAncestorFilterValues
      levelNum: number
      label: string
    }> = []

    if (level >= 2) {
      fields.push({
        key: 'level_1_id',
        levelNum: 1,
        label: labelAt(labels, 1),
      })
    }
    if (level >= 3) {
      fields.push({
        key: 'level_2_id',
        levelNum: 2,
        label: labelAt(labels, 2),
      })
    }
    if (level >= 4) {
      fields.push({
        key: 'level_3_id',
        levelNum: 3,
        label: labelAt(labels, 3),
      })
    }
    return fields
  }, [level, labels])

  const handleSelect = (
    key: keyof HierarchyAncestorFilterValues,
    option: SelectOption | null
  ) => {
    const next = { ...values, [key]: option?.value ?? '' }
    if (key === 'level_1_id') {
      next.level_2_id = ''
      next.level_3_id = ''
    } else if (key === 'level_2_id') {
      next.level_3_id = ''
    }
    onChange(next)
  }

  if (level <= 1) {
    return (
      <div className="hierarchy_filters">
        <div className="hierarchy_filter_field">
          <label>Subject</label>
          <div className="hierarchy_filter_subject">{subject.title}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="hierarchy_filters">
      <div className="hierarchy_filter_field">
        <label>Subject</label>
        <div className="hierarchy_filter_subject">{subject.title}</div>
      </div>

      {filterFields.map(field => {
        const selected = values[field.key]
          ? options[field.levelNum]?.find(
              option => option.value === values[field.key]
            ) ?? { value: values[field.key], label: values[field.key] }
          : null

        const isDisabled =
          (field.key === 'level_2_id' && !values.level_1_id) ||
          (field.key === 'level_3_id' && !values.level_2_id)

        return (
          <div className="hierarchy_filter_field" key={field.key}>
            <label>{field.label}</label>
            <div
              style={
                isDisabled ? { opacity: 0.6, pointerEvents: 'none' } : undefined
              }
            >
              <CustomSelect
                data={[
                  { label: `All ${field.label.toLowerCase()}s`, value: '' },
                  ...(options[field.levelNum] || []),
                ]}
                value={selected}
                placeholder={`All ${field.label.toLowerCase()}s`}
                onChange={option =>
                  handleSelect(
                    field.key,
                    option && option.value ? (option as SelectOption) : null
                  )
                }
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default HierarchyAncestorFilters
